/** Shared dark-theme class tokens for UI primitives (Phase 2). */

export const disabledClass = 'opacity-40 cursor-not-allowed pointer-events-none';

export const button = {
  primary:
    'bg-brand-purple hover:bg-purple-700 text-white font-medium rounded-lg px-4 py-2 transition-all duration-200 shadow-lg hover:shadow-brand-purple/30 hover:shadow-xl',
  secondary:
    'bg-dark-elevated border border-dark-border hover:border-brand-purple text-slate-300 hover:text-white font-medium rounded-lg px-4 py-2 transition-all duration-200',
  ghost:
    'text-slate-400 hover:text-white hover:bg-dark-elevated rounded-lg px-4 py-2 transition-all duration-200',
  destructive:
    'bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg px-4 py-2 transition-all duration-200',
  icon: 'text-slate-400 hover:text-white hover:bg-dark-elevated rounded-lg p-2 transition-all duration-200',
  primarySm:
    'bg-brand-purple hover:bg-purple-700 text-white font-medium rounded-lg px-3 py-1.5 text-sm transition-all duration-200 shadow-lg hover:shadow-brand-purple/30',
  secondarySm:
    'bg-dark-elevated border border-dark-border hover:border-brand-purple text-slate-300 hover:text-white font-medium rounded-lg px-3 py-1.5 text-sm transition-all duration-200',
};

export const input =
  'block w-full bg-dark-elevated border border-dark-border rounded-lg px-3 py-2 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-brand-purple focus:ring-1 focus:ring-brand-purple/30 transition-all duration-200';

export const inputCompact =
  'block w-full bg-dark-elevated border border-dark-border rounded-lg px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-brand-purple focus:ring-1 focus:ring-brand-purple/30 transition-all duration-200';

export const select = input;

export const textarea = `${input} min-h-[80px] resize-y`;

export const card = {
  default: 'bg-dark-surface border border-dark-border rounded-xl p-4',
  hoverable:
    'bg-dark-surface border border-dark-border rounded-xl p-4 hover:border-dark-border-glow hover:shadow-lg hover:shadow-black/30 transition-all duration-200 cursor-pointer',
  glass: 'backdrop-blur-md border rounded-xl p-4',
  elevated: 'bg-dark-elevated border border-dark-border rounded-xl shadow-2xl shadow-black/60 p-4',
};

export const glassStyle = {
  background: 'var(--glass-bg)',
  borderColor: 'var(--glass-border)',
};

export const badge = {
  default:
    'bg-dark-elevated text-slate-300 border border-dark-border text-xs font-medium rounded-full px-2.5 py-0.5',
  purple:
    'bg-brand-purple/15 text-purple-300 border border-brand-purple/30 text-xs font-medium rounded-full px-2.5 py-0.5',
  teal: 'bg-teal-900/30 text-teal-300 border border-teal-700/30 text-xs font-medium rounded-full px-2.5 py-0.5',
  success:
    'bg-emerald-900/30 text-emerald-300 border border-emerald-700/30 text-xs font-medium rounded-full px-2.5 py-0.5',
  warning:
    'bg-amber-900/30 text-amber-300 border border-amber-700/30 text-xs font-medium rounded-full px-2.5 py-0.5',
  error:
    'bg-red-900/30 text-red-300 border border-red-700/30 text-xs font-medium rounded-full px-2.5 py-0.5',
  sky: 'bg-sky-900/40 text-sky-200 text-xs font-medium rounded-full px-2.5 py-0.5',
  violet: 'bg-violet-900/40 text-violet-100 text-xs font-medium rounded-full px-2.5 py-0.5',
  slate: 'bg-slate-700 text-slate-200 text-xs font-medium rounded-full px-2.5 py-0.5',
  info: 'bg-blue-900/40 text-blue-200 text-xs font-medium rounded-full px-2.5 py-0.5',
};

export const alert = {
  error: 'rounded-lg border border-red-900/50 bg-red-950/30 text-red-100',
  success: 'rounded-lg border border-emerald-900/50 bg-emerald-950/30 text-emerald-100',
  warning: 'rounded-lg border border-amber-900/50 bg-amber-950/30 text-amber-100',
  info: 'rounded-lg border border-blue-900/50 bg-blue-950/30 text-blue-100',
};

export const destructive = {
  text: 'text-red-400',
  iconHover: 'text-red-400 hover:bg-red-900/20',
  rowHover: 'text-red-400 hover:bg-red-900/20',
  menuItem: 'text-red-300 hover:bg-red-900/20',
};

export const link = {
  mutedHover: 'text-slate-400 hover:text-slate-300 hover:border-dark-border',
  accentHover: 'hover:text-vivid-red',
  accentText: 'text-vivid-red hover:text-vivid-red',
};

export const focus = {
  ringOffset: 'focus-visible:ring-offset-dark-base',
  ringOffsetSm: 'focus-visible:ring-offset-2 focus-visible:ring-offset-dark-base',
};

export const pageTab = {
  base: 'tab-trigger-mdl inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors shrink-0',
  active: 'border-vivid-red text-vivid-red',
  inactive: 'border-transparent text-slate-400 hover:text-slate-300 hover:border-dark-border',
};

export const requiredMark = 'text-red-400';

export const modal = {
  overlay: 'fixed inset-0 bg-black/60 backdrop-blur-sm z-50',
  container:
    'bg-dark-elevated border border-dark-border rounded-xl shadow-2xl shadow-black/60 w-full mx-auto',
  header: 'border-b border-dark-border px-6 py-4',
  footer: 'border-t border-dark-border px-6 py-4 flex justify-end gap-3',
};

export const dropdown = {
  container:
    'bg-dark-elevated border border-dark-border rounded-lg shadow-xl shadow-black/50 py-1 min-w-48',
  item: 'px-3 py-2 text-sm text-slate-300 hover:bg-dark-base hover:text-white transition-colors duration-150 cursor-pointer',
  itemActive:
    'px-3 py-2 text-sm bg-dark-base text-white transition-colors duration-150 cursor-pointer',
  separator: 'border-t border-dark-border my-1',
  destructive:
    'px-3 py-2 text-sm text-red-400 hover:bg-red-900/20 hover:text-red-300 transition-colors duration-150 cursor-pointer',
};

export const tooltip =
  'bg-dark-elevated border border-dark-border text-slate-200 text-xs rounded-lg px-2.5 py-1.5 shadow-xl';

export const skeleton = {
  block: 'bg-dark-elevated animate-pulse rounded',
  shimmer:
    'bg-gradient-to-r from-dark-elevated via-dark-border to-dark-elevated bg-[length:200%_100%] animate-pulse rounded',
};

export const table = {
  wrapper: 'bg-dark-surface border border-dark-border rounded-xl overflow-hidden',
  thead: 'bg-dark-elevated',
  th: 'text-slate-400 text-xs font-medium uppercase tracking-wider px-4 py-3 text-left border-b border-dark-border',
  td: 'px-4 py-3 text-slate-300 text-sm border-b border-dark-border',
  rowHover: 'hover:bg-dark-elevated/50 transition-colors duration-150',
  rowSelected: 'bg-brand-purple/10 border-l-2 border-brand-purple',
};

export const toggle = {
  trackOff: 'bg-dark-border',
  trackOn: 'bg-brand-purple',
  thumb: 'bg-white shadow-sm',
};

export const checkbox = {
  unchecked: 'border-dark-border bg-dark-elevated',
  checked: 'bg-brand-purple border-brand-purple',
};

export const tabs = {
  list: 'bg-dark-base border-b border-dark-border',
  inactive: 'text-slate-400 hover:text-slate-200 px-4 py-2.5 text-sm font-medium transition-colors',
  active: 'text-white border-b-2 border-brand-purple px-4 py-2.5 text-sm font-medium',
  pillList: 'bg-dark-elevated rounded-lg p-1 flex gap-1',
  pillInactive: 'text-slate-400 hover:text-slate-200 px-3 py-1.5 text-sm rounded-md transition-all',
  pillActive:
    'bg-dark-base text-white border border-dark-border-glow px-3 py-1.5 text-sm rounded-md shadow-sm',
};

/** Merge class strings, skipping falsy values. */
export function cn(...parts) {
  return parts.filter(Boolean).join(' ');
}
